import { clsx } from 'clsx';
import { format, isSameDay } from 'date-fns';
import { useMemo } from 'react';
import {
  eventColors,
  eventColorsDark,
  eventStateLabel,
  eventStatusContent,
  eventStatusWrapper,
} from 'src/components/Events/EventStatus/EventStatus.css';
import { Content, useFormattedContent } from 'src/contexts/ContentContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { EVENT_STATUS_TO_CID } from 'src/utils/constants/contentIdMaps';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { getLocaleFromLanguageOrCurrent } from 'src/utils/localeUtils';
import { ApiEventStatus, Feature } from 'src/WebApiController';

import { EventStatusProps, EventStatusToColor } from './EventStatus.type';

const DateRelatedStatuses: ApiEventStatus[] = [ApiEventStatus.Rescheduled];

export const EventStatus = ({
  status,
  originalStartDate,
  currentStartDate,
}: EventStatusProps) => {
  const { isDarkMode } = useSiteTheme();
  const hasShowOriginalStartDate = useUserHasFeature(
    Feature.ShowEventOriginalStartDate
  );

  const eventColorClassname = useMemo(() => {
    if (!status) {
      return undefined;
    }
    if (isDarkMode) {
      return eventColorsDark[EventStatusToColor[status]];
    }
    return eventColors[EventStatusToColor[status]];
  }, [isDarkMode, status]);

  const showRescheduledDate = useMemo(() => {
    if (
      !hasShowOriginalStartDate ||
      !originalStartDate ||
      !status ||
      !DateRelatedStatuses.includes(status)
    ) {
      return false;
    }
    // Don't show the original date suffix when only the time changed, not the day
    if (
      currentStartDate &&
      isSameDay(new Date(originalStartDate), new Date(currentStartDate))
    ) {
      return false;
    }
    return true;
  }, [hasShowOriginalStartDate, originalStartDate, currentStartDate, status]);

  const formattedOriginalDate = useMemo(() => {
    if (!showRescheduledDate || !originalStartDate) {
      return '';
    }
    const locale = getLocaleFromLanguageOrCurrent();
    return format(new Date(originalStartDate), 'MMM d, y', { locale });
  }, [showRescheduledDate, originalStartDate]);

  const rescheduledFromDateText = useFormattedContent(
    FormatContentId.RescheduledFromDate,
    formattedOriginalDate
  );

  // Don't show normal state
  if (!status || status === ApiEventStatus.Normal) {
    return null;
  }

  return (
    <div className={eventStatusWrapper}>
      <div className={clsx(eventStatusContent, eventColorClassname)}>
        <span className={eventStateLabel}>
          {showRescheduledDate ? (
            rescheduledFromDateText
          ) : (
            <Content id={EVENT_STATUS_TO_CID[status]} />
          )}
        </span>
      </div>
    </div>
  );
};
